import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { Panel, TactLoader } from "./Tactical";
import { toast } from "sonner";
import { ImagePlus, Send, Pencil, Trash2, Check, X, Megaphone, Video, BarChart3 } from "lucide-react";
import PostComments from "./PostComments";
import RichText, { detectDir } from "./RichText";
import LinkEmbed from "./LinkEmbed";
import LikeButton from "./LikeButton";
import PollCard from "./PollCard";
import PollComposer from "./PollComposer";

function FeedVideo({ path, contentType }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true; let created = null;
    (async () => {
      try {
        const resp = await api.get(`/files/${path}`, { responseType: "blob" });
        created = URL.createObjectURL(resp.data);
        if (alive) setUrl(created);
      } catch (e) { /* noop */ }
    })();
    return () => { alive = false; if (created) URL.revokeObjectURL(created); };
  }, [path]);
  if (!url) return <div className="font-mono text-[10px] text-[#71717A]">[ LOADING VIDEO… ]</div>;
  return (
    <video
      src={url}
      controls
      playsInline
      preload="metadata"
      className="w-full max-h-[480px] bg-black border border-[#E4E4E7] dark:border-[#2A2A30]"
      data-testid="global-feed-video"
    >
      {contentType && <source src={url} type={contentType} />}
      Your browser does not support video.
    </video>
  );
}

function FeedImage({ path }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true; let created = null;
    (async () => {
      try {
        const resp = await api.get(`/files/${path}`, { responseType: "blob" });
        created = URL.createObjectURL(resp.data);
        if (alive) setUrl(created);
      } catch (e) { /* noop */ }
    })();
    return () => { alive = false; if (created) URL.revokeObjectURL(created); };
  }, [path]);
  if (!url) return <div className="font-mono text-[10px] text-[#71717A]">[ LOADING IMAGE… ]</div>;
  return <img src={url} alt="post" className="w-full max-h-[480px] object-contain border border-[#E4E4E7] dark:border-[#2A2A30] bg-[#FAFAFC] dark:bg-[#09090B]" data-testid="global-feed-image" />;
}

export default function GlobalFeed() {
  const { user } = useAuth();
  const { t } = useT();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [composerTab, setComposerTab] = useState("announcement"); // or "poll"
  const fileRef = useRef();
  const videoRef = useRef();

  const refresh = async () => {
    try {
      const { data } = await api.get("/global-feed");
      setPosts(data);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 15000);
    return () => clearInterval(iv);
  }, []);

  const isAdmin = user?.role === "admin";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim() && !file && !videoFile) {
      toast.error(t("feed.placeholder"));
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("text", text.trim());
      if (file) form.append("image", file);
      if (videoFile) form.append("video", videoFile);
      await api.post("/global-feed", form, { headers: { "Content-Type": "multipart/form-data" } });
      setText(""); setFile(null); setVideoFile(null);
      if (fileRef.current) fileRef.current.value = "";
      if (videoRef.current) videoRef.current.value = "";
      await refresh();
      toast.success(t("feed.posted"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const onPollCreated = async () => {
    await refresh();
    setComposerTab("announcement");
  };

  const updatePostLocal = (id, patch) => {
    setPosts((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };

  const startEdit = (p) => { setEditingId(p.id); setEditText(p.text); };
  const cancelEdit = () => { setEditingId(null); setEditText(""); };
  const saveEdit = async (id) => {
    try {
      await api.patch(`/feed/${id}`, { text: editText.trim() });
      setEditingId(null); setEditText("");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removePost = async (id) => {
    if (!window.confirm(t("feed.deleteConfirm"))) return;
    try {
      await api.delete(`/feed/${id}`);
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section data-testid="global-feed" className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-[#E11D48] flex items-center justify-center" aria-hidden>
          <Megaphone className="w-4 h-4 text-[#09090B] dark:text-[#F4F4F5]" />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E11D48]">{t("feed.kicker")}</div>
          <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-wider">{t("feed.title")}</h2>
        </div>
      </div>

      {isAdmin && (
        <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-t-2 border-t-[#E11D48] p-5 space-y-4" data-testid="global-feed-composer">
          <div className="flex gap-2 border-b border-[#E4E4E7] dark:border-[#2A2A30] pb-3" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={composerTab === "announcement"}
              data-testid="composer-tab-announcement"
              onClick={() => setComposerTab("announcement")}
              className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 transition-colors ${
                composerTab === "announcement"
                  ? "bg-[#E11D48] text-white"
                  : "border border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A] hover:text-[#E11D48]"
              }`}
            >
              <Megaphone className="w-3 h-3" /> {t("feed.announceTab")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={composerTab === "poll"}
              data-testid="composer-tab-poll"
              onClick={() => setComposerTab("poll")}
              className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 transition-colors ${
                composerTab === "poll"
                  ? "bg-[#7C3AED] text-white"
                  : "border border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A] hover:text-[#7C3AED]"
              }`}
            >
              <BarChart3 className="w-3 h-3" /> {t("feed.pollTab")}
            </button>
          </div>

          {composerTab === "announcement" ? (
            <form onSubmit={onSubmit} className="space-y-3" data-testid="global-feed-form">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E11D48]">{t("feed.adminKicker")}</div>
              <textarea
                data-testid="global-feed-text"
                value={text} onChange={(e) => setText(e.target.value)}
                dir={detectDir(text) === "rtl" ? "rtl" : "auto"}
                placeholder={t("feed.placeholder")}
                maxLength={4000} rows={3}
                className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-3 text-[#09090B] dark:text-[#F4F4F5] font-body focus:outline-none focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED] resize-none"
              />
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="cursor-pointer inline-flex items-center gap-2 border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-xs uppercase tracking-widest hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors font-body">
                    <input ref={fileRef} data-testid="global-feed-image-input" type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                    <ImagePlus className="w-3 h-3" />
                    {file ? file.name.slice(0, 18) : t("feed.addImage")}
                  </label>
                  <label className="cursor-pointer inline-flex items-center gap-2 border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-xs uppercase tracking-widest hover:border-[#EA580C] hover:text-[#EA580C] transition-colors font-body">
                    <input ref={videoRef} data-testid="global-feed-video-input" type="file" accept="video/*" className="hidden" onChange={(e) => setVideoFile(e.target.files?.[0] || null)} />
                    <Video className="w-3 h-3" />
                    {videoFile ? videoFile.name.slice(0, 18) : t("feed.addVideo")}
                  </label>
                </div>
                <button data-testid="global-feed-submit-btn" type="submit" disabled={submitting} className="bg-[#7C3AED] text-white font-heading uppercase tracking-widest px-5 py-2 hover:bg-[#6D28D9] transition-colors disabled:opacity-50 inline-flex items-center gap-2">
                  <Send className="w-3 h-3" /> {submitting ? t("feed.posting") : t("feed.broadcast")}
                </button>
              </div>
            </form>
          ) : (
            <PollComposer onCreated={onPollCreated} />
          )}
        </div>
      )}

      {loading ? <TactLoader /> : posts.length === 0 ? (
        <Panel className="font-mono text-[#71717A] text-sm">{t("feed.empty")}</Panel>
      ) : (
        <div className="space-y-3" data-testid="global-feed-posts">
          {posts.map((p) => {
            const editing = editingId === p.id;
            const isPoll = p.type === "poll";
            return (
              <article key={p.id} className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-l-2 border-l-[#E11D48] p-5" data-testid={`global-post-${p.id}`}>
                <header className="flex items-center justify-between mb-3 gap-2">
                  <div>
                    <div className="font-heading text-lg uppercase">{p.username} <span className="font-mono text-[10px] text-[#E11D48] tracking-[0.2em] ml-2">// {t("pp.admin")}</span></div>
                    <div className="font-mono text-[10px] text-[#71717A] tracking-[0.2em]">
                      {new Date(p.created_at).toLocaleString()}{p.edited_at ? " · edited" : ""}
                    </div>
                  </div>
                  {isAdmin && !editing && !isPoll && (
                    <div className="flex gap-1">
                      <button data-testid={`global-edit-${p.id}`} onClick={() => startEdit(p)} className="border border-[#E4E4E7] dark:border-[#2A2A30] p-2 hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors" title={t("feed.edit")}><Pencil className="w-3 h-3" /></button>
                      <button data-testid={`global-delete-${p.id}`} onClick={() => removePost(p.id)} className="border border-[#E4E4E7] dark:border-[#2A2A30] p-2 hover:border-[#E11D48] hover:text-[#E11D48] transition-colors" title={t("feed.delete")}><Trash2 className="w-3 h-3" /></button>
                    </div>
                  )}
                  {isAdmin && isPoll && (
                    <button data-testid={`global-delete-${p.id}`} onClick={() => removePost(p.id)} className="border border-[#E4E4E7] dark:border-[#2A2A30] p-2 hover:border-[#E11D48] hover:text-[#E11D48] transition-colors" title={t("feed.delete")}><Trash2 className="w-3 h-3" /></button>
                  )}
                </header>

                {isPoll ? (
                  <PollCard post={p} onUpdate={(poll) => updatePostLocal(p.id, { poll })} />
                ) : editing ? (
                  <div className="space-y-2">
                    <textarea
                      data-testid={`global-edit-text-${p.id}`}
                      value={editText} onChange={(e) => setEditText(e.target.value)}
                      dir={detectDir(editText) === "rtl" ? "rtl" : "auto"}
                      maxLength={4000} rows={3}
                      className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-2 text-[#09090B] dark:text-[#F4F4F5] font-body focus:outline-none focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED] resize-none"
                    />
                    <div className="flex justify-end gap-2">
                      <button onClick={cancelEdit} className="border border-[#E4E4E7] dark:border-[#2A2A30] p-2 hover:border-[#E11D48] hover:text-[#E11D48] transition-colors"><X className="w-3 h-3" /></button>
                      <button data-testid={`global-edit-save-${p.id}`} onClick={() => saveEdit(p.id)} className="bg-[#7C3AED] text-white p-2 hover:bg-[#6D28D9] transition-colors"><Check className="w-3 h-3" /></button>
                    </div>
                  </div>
                ) : (
                  p.text && <RichText text={p.text} className="text-[#09090B] dark:text-[#F4F4F5] font-body" testId={`global-post-text-${p.id}`} />
                )}

                {!isPoll && p.text && !editing && (
                  <div className="mt-3"><LinkEmbed text={p.text} /></div>
                )}

                {!isPoll && p.image_path && !editing && (
                  <div className="mt-3"><FeedImage path={p.image_path} /></div>
                )}
                {!isPoll && p.video_path && !editing && (
                  <div className="mt-3"><FeedVideo path={p.video_path} contentType={p.video_content_type} /></div>
                )}

                {/* Actions: like + comments */}
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <LikeButton post={p} onChange={({ liked, count }) => {
                    const likes = (p.likes || []).filter((u) => u !== user?.id);
                    if (liked && user?.id) likes.push(user.id);
                    updatePostLocal(p.id, { likes });
                  }} />
                </div>

                <PostComments postId={p.id} isPublic />
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
